import moment from "moment";
import { call, put, select, takeEvery } from "redux-saga/effects";
import {
    appInitAction,
    loadAccountHistoryFailure,
    loadAccountHistoryInfoFailure,
    loadAccountHistoryInfoSuccess,
    loadAccountHistorySuccess,
    openModalAction
} from "../../app/appActions";
import { LOAD_ACCOUNT_HISTORY_INFO_START, LOAD_ACCOUNT_HISTORY_START } from "../../app/appConstants";
import { getIsUserBuyerSelector, getUserEmailSelector } from "../../app/appSelectors";
import { failurePut } from "../../app/failurePut";
import { getRequest, postRequest } from "../../app/request";
import { MOBILE_SLIDER_PAGES_IDS } from "../../mobileAppCore/components/MobileSliderPages/mobileSliderPagesConstants";
import { openMobileSliderPageAction } from "../../mobileAppCore/mobileAppActions";
import { MODALS_NAME } from "../Modals/modalsConstants";
import {
    accountGetPortfolioFailure,
    accountGetPortfolioSuccess,
    accountGetTradingTokenFailure,
    accountGetTradingTokenSuccess,
    depositCreateFailure,
    depositCreateSuccess,
    getDepositAvailableMethodsFailure,
    getDepositAvailableMethodsSuccess,
    getDepositFormFailure,
    getDepositFormSuccess,
    getDepositParamsFailure,
    getDepositParamsSuccess,
    getDepositSurveyFailure,
    getPayoutParamsFailure,
    getPayoutParamsSuccess,
    payoutCreateFailure,
    payoutCreateSuccess,
    payoutGetAvailableMethodsFailure,
    payoutGetAvailableMethodsSuccess,
    payoutGetFormFailure,
    payoutGetFormSuccess,
    payoutGetTransactionsFailure,
    payoutGetTransactionsSuccess,
    resendUserConfirmEmailFailure,
    resendUserConfirmEmailSuccess,
    sendDepositSurveyResultFailure,
    sendDepositSurveyResultSuccess,
    setConfirmEmailRequestedTimeAction,
    setIsCheckDepositSurvey,
} from "./accountActions";
import {
    ACCOUNT_GET_PORTFOLIO_START,
    ACCOUNT_GET_TRADING_TOKEN_START,
    DEPOSIT_CREATE_MOBILE_START,
    DEPOSIT_CREATE_WEB_START,
    GET_DEPOSIT_AVAILABLE_METHODS_START,
    GET_DEPOSIT_FORM_START,
    GET_DEPOSIT_PARAMS_START,
    GET_DEPOSIT_SURVEY_START,
    GET_PAYOUT_PARAMS_START,
    PAYOUT_CREATE_START,
    PAYOUT_GET_AVAILABLE_METHODS_START,
    PAYOUT_GET_FORM_START,
    PAYOUT_TRANSACTIONS_START,
    RESEND_USER_CONFIRM_EMAIL_START,
    SEND_DEPOSIT_SURVEY_RESULT_START,
} from "./accountConstants";
import { bonusesSetActiveListSuccess, bonusesSetAvailableList } from "../Bonus/bonusesActions";
import { getIsCheckDepositSurvey } from "./accountSelectors";

export function* accountGetPortfolioSaga() {
    try {
        const data = yield call(getRequest, '/portfolio');

        yield put(accountGetPortfolioSuccess(data));
        yield put(bonusesSetActiveListSuccess(data.bonusesActive));
        yield put(bonusesSetAvailableList(data.bonusesAvailable))
    } catch (e) {
        yield call(failurePut, e, accountGetPortfolioFailure);
    }
}

export function* accountGetTradingTokenSaga() {
    try {
        const data = yield call(getRequest, '/tradingToken');
        yield put(accountGetTradingTokenSuccess(data));
    } catch (e) {
        yield call(failurePut, e, accountGetTradingTokenFailure);
    }
}

export function* getDepositFormSaga({ payload }) {
    try {
        const data = yield call(getRequest, `/deposit/form?payment=${payload.payment}`);

        yield put(getDepositFormSuccess(data));
    } catch (e) {
        yield call(failurePut, e, getDepositFormFailure);
    }
}

export function* getDepositParamsSaga() {
    try {
        const data = yield call(getRequest, '/deposit');

        yield put(getDepositParamsSuccess(data));
    } catch (e) {
        yield call(failurePut, e, getDepositParamsFailure);
    }
}

export function* depositGetAvailableMethodsSaga({ payload }) {
    try {
        const data = yield call(postRequest, '/deposit/available_methods', {
            country: payload.country,
        });

        yield put(getDepositAvailableMethodsSuccess(data.filter(item => !!item.type)));
    } catch (e) {
        yield call(failurePut, e, getDepositAvailableMethodsFailure);
    }
}

export function* depositCreateWebSaga({ payload }) {
    let depositWindow;
    if (payload.paymentType === 'redirect') {
        depositWindow = window.open(`${document.location.protocol}//${document.location.host}/redirect-billing`);
    }
    try {
        const response = yield call(postRequest, '/deposit/create', {
            amount: payload.amount,
            currency: payload.currency,
            payment_id: payload.payment_id,
            payment_key: payload.payment_key,
            payment_name: payload.payment_name,
            form_data: payload.form_data,
            apply_bonus: payload.apply_bonus,
        });
        yield put(depositCreateSuccess({ ...payload, ...response }));
        if (response.qr_code_picture) {
            // nothing
        } else if (response.url) {
            if (response.url.indexOf('//') === -1) {
                response.url = 'https://' + response.url;
            }
            depositWindow?.location?.assign && depositWindow.location.assign(response.url);
            yield put(openModalAction({
                name: MODALS_NAME.CASHIER,
                tab: 'history'
            }));
        } else {
            if (depositWindow) {
                depositWindow.close();
            }
            yield put(depositCreateFailure('error.default_message'));
        }
        yield put(setIsCheckDepositSurvey(true));
    } catch (e) {
        if (depositWindow) {
            depositWindow.close();
        }
        yield call(failurePut, e, depositCreateFailure);
    }
}

export function* depositCreateMobileSaga({ payload }) {
    try {
        const response = yield call(postRequest, '/deposit/create', {
            amount: payload.amount,
            currency: payload.currency,
            payment_id: payload.payment_id,
            payment_key: payload.payment_key,
            payment_name: payload.payment_name,
            form_data: payload.form_data,
            apply_bonus: payload.apply_bonus,
        });
        yield put(depositCreateSuccess({ ...payload, ...response }));
        if (response.qr_code_picture) {
            // nothing
        } else if (response.url) {
            if (response.url.indexOf('//') === -1) {
                response.url = 'https://' + response.url;
            }
            window.open(response.url);
            yield put(openMobileSliderPageAction(MOBILE_SLIDER_PAGES_IDS.HISTORY));
        } else {
            yield put(depositCreateFailure('error.default_message'));
        }
        yield put(setIsCheckDepositSurvey(true));
    } catch (e) {
        yield call(failurePut, e, depositCreateFailure);
    }
}

export function* getDepositSurveySaga({ payload }) {
    const isUserBuyer = yield select(getIsUserBuyerSelector);
    const isCheckDepositSurvey = yield select(getIsCheckDepositSurvey);
    yield put(setIsCheckDepositSurvey(false));

    if (!isCheckDepositSurvey || isUserBuyer) {
        return;
    }

    try {
        const data = yield call(getRequest, '/deposit/survey');
        if (data.show_deposit_survey) {
            payload.onShowDepositSurvey && payload.onShowDepositSurvey();
        }
        yield put(getDepositSurveyFailure(data));
    } catch (e) {
        yield call(failurePut, e, getDepositSurveyFailure);
    }
}

export function* sendDepositSurveyResultSaga({ payload }) {
    try {
        yield call(postRequest, '/deposit/survey', payload);
        yield put(sendDepositSurveyResultSuccess());
    } catch (e) {
        yield call(failurePut, e, sendDepositSurveyResultFailure);
    }
}

export function* payoutGetAvailableMethodsSaga({ payload }) {
    try {
        const data = yield call(postRequest, '/payout/available_methods', {
            country: payload.country,
        });

        yield put(payoutGetAvailableMethodsSuccess(data.filter(item => !!item.type)));
    } catch (e) {
        yield call(failurePut, e, payoutGetAvailableMethodsFailure);
    }
}

export function* payoutGetFormSaga({ payload }) {
    try {
        const data = yield call(getRequest, `/payout/form?payment=${payload.payment}`);

        yield put(payoutGetFormSuccess(data));
    } catch (e) {
        yield call(failurePut, e, payoutGetFormFailure);
    }
}

export function* payoutCreateSaga({ payload }) {
    const { body, callback } = payload;
    try {
        const data = yield call(postRequest, '/payout/create', body);
        yield put(payoutCreateSuccess(data));
        if (callback) {
            callback();
        }
    } catch (e) {
        yield call(failurePut, e, payoutCreateFailure);
    }
}

export function* getPayoutParamsSaga() {
    try {
        const data = yield call(getRequest, '/payout');

        yield put(getPayoutParamsSuccess(data));
    } catch (e) {
        yield call(failurePut, e, getPayoutParamsFailure);
    }
}

export function* payoutGetTransactionsSaga() {
    try {
        const data = yield call(getRequest, '/transactions');
        yield put(payoutGetTransactionsSuccess(data));
    } catch (e) {
        yield call(failurePut, e, payoutGetTransactionsFailure);
    }
}

export function* resendUserConfirmEmailSaga() {
    try {
        const email = yield select(getUserEmailSelector);
        yield call(postRequest, '/user/resend', { email, requestType: 'confirm' });
        yield put(resendUserConfirmEmailSuccess());
    } catch (e) {
        if (e.response?.data?.messageCode === 'error.email_already_confirmed') {
            yield put(appInitAction());
        }
        yield call(failurePut, e, resendUserConfirmEmailFailure);
    } finally {
        const currentTime = moment().toISOString();
        yield put(setConfirmEmailRequestedTimeAction(currentTime));
    }
}

export function* loadAccountHistorySaga({ payload }) {
    try {
        const accountCode = encodeURIComponent(payload);
        const data = yield call(getRequest, `/account/${accountCode}/history`);
        yield put(loadAccountHistorySuccess(data));
    } catch (e) {
        yield call(failurePut, e, loadAccountHistoryFailure);
    }
}

export function* loadAccountHistoryInfoSaga({ payload }) {
    try {
        const accountCode = encodeURIComponent(payload.accountCode);
        const data = yield call(getRequest, `/account/${accountCode}/history/${payload.type}/${payload.id}`);
        yield put(loadAccountHistoryInfoSuccess({ type: payload.type, info: data || {} }));
    } catch (e) {
        yield call(failurePut, e, loadAccountHistoryInfoFailure);
    }
}

export function* accountSagas() {
    yield takeEvery(ACCOUNT_GET_PORTFOLIO_START, accountGetPortfolioSaga);
    yield takeEvery(ACCOUNT_GET_TRADING_TOKEN_START, accountGetTradingTokenSaga);

    yield takeEvery(PAYOUT_TRANSACTIONS_START, payoutGetTransactionsSaga);

    yield takeEvery(GET_DEPOSIT_FORM_START, getDepositFormSaga);
    yield takeEvery(GET_DEPOSIT_PARAMS_START, getDepositParamsSaga);
    yield takeEvery(GET_DEPOSIT_AVAILABLE_METHODS_START, depositGetAvailableMethodsSaga);
    yield takeEvery(DEPOSIT_CREATE_WEB_START, depositCreateWebSaga);
    yield takeEvery(DEPOSIT_CREATE_MOBILE_START, depositCreateMobileSaga);

    yield takeEvery(GET_DEPOSIT_SURVEY_START, getDepositSurveySaga);
    yield takeEvery(SEND_DEPOSIT_SURVEY_RESULT_START, sendDepositSurveyResultSaga);

    yield takeEvery(GET_PAYOUT_PARAMS_START, getPayoutParamsSaga);
    yield takeEvery(PAYOUT_GET_AVAILABLE_METHODS_START, payoutGetAvailableMethodsSaga);
    yield takeEvery(PAYOUT_GET_FORM_START, payoutGetFormSaga);
    yield takeEvery(PAYOUT_CREATE_START, payoutCreateSaga);

    yield takeEvery(RESEND_USER_CONFIRM_EMAIL_START, resendUserConfirmEmailSaga);

    yield takeEvery(LOAD_ACCOUNT_HISTORY_START, loadAccountHistorySaga);
    yield takeEvery(LOAD_ACCOUNT_HISTORY_INFO_START, loadAccountHistoryInfoSaga)
}
